import { useId, useState, type FormEvent } from "react";
import { Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpsertDepartmentOverride } from "@/hooks/useSupportDepartments";
import type { Department } from "@/services/supportDepartmentsService";

import { apiErrorMessage, isValidSlackUserId } from "./slackId";

// Same rule as the backend's check, so a bad address is caught before the request.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Add a person to a department in Core, whatever their Teamwork role. */
export function AddPersonForm({ department }: { department: Department }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [slackId, setSlackId] = useState("");
  const [lead, setLead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upsert = useUpsertDepartmentOverride();
  const id = useId();

  const reset = () => {
    setName("");
    setEmail("");
    setSlackId("");
    setLead(false);
    setError(null);
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-2"
        aria-label={`Add person to ${department.label}`}
        onClick={() => setOpen(true)}
      >
        <UserPlus className="mr-1 h-4 w-4" /> Add person
      </Button>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanSlack = slackId.trim();
    if (!EMAIL_RE.test(cleanEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    if (cleanSlack && !isValidSlackUserId(cleanSlack)) {
      setError("Slack id must look like U0123ABCD (or leave it empty).");
      return;
    }
    setError(null);
    try {
      await upsert.mutateAsync({
        department: department.key,
        input: {
          email: cleanEmail,
          display_name: name.trim(),
          mode: "include",
          is_lead: lead,
          slack_user_id: cleanSlack || null,
        },
      });
      reset();
    } catch (err) {
      setError(apiErrorMessage(err, "the person was not added."));
    }
  };

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`${id}-name`}>Name</Label>
          <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${id}-email`}>Email</Label>
          <Input id={`${id}-email`} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${id}-slack`}>Slack id (optional)</Label>
          <Input
            id={`${id}-slack`}
            value={slackId}
            placeholder="U0123ABCD"
            onChange={(e) => setSlackId(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Checkbox id={`${id}-lead`} checked={lead} onCheckedChange={(v) => setLead(v === true)} />
          <Label htmlFor={`${id}-lead`}>Lead</Label>
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={upsert.isPending}>
          {upsert.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          Add
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
